'use client'

import Link from 'next/link'

import { hasText } from '@payloadcms/richtext-lexical/shared'

import { BillApproval } from '@/components/legislative-tracker/BillApproval'
import { TrackTag } from '@/components/legislative-tracker/BillCard/components/TrackTag'
import { BillSponsorsSection } from '@/components/legislative-tracker/BillDetails/components/BillSponsorsSection'
import { BillStatus } from '@/components/legislative-tracker/BillStatus'
// import { RevealSection } from '@/components/RevealSection'
import RichText from '@/components/RichText'
import { Button } from '@/components/ui/button'
import { useUserTracking } from '@/providers/UserTracking'

import type { Bill } from '@/payload-types'
import type { BillProgressTimelineEntry } from '@/utilities/billTrack50/getBillProgressTimeline'
import type { DefaultTypedEditorState } from '@payloadcms/richtext-lexical'

export const BillInfo = ({
  bill,
  billStatus,
}: {
  bill: Bill
  billStatus: BillProgressTimelineEntry[] | null
}) => {
  const { trackedBillIds } = useUserTracking() ?? {}
  const isTracked = Boolean(trackedBillIds?.includes(bill.id))
  const billSummary = hasText(bill.description) ? bill.description : null
  const mahaApproval = bill?.mahaApproved || null

  return (
    <div className="self-start rounded-xl md:border md:border-border md:p-6">
      <div className="mb-6 flex flex-col items-start justify-between gap-3 md:mb-10 md:flex-row md:items-center lg:mb-14">
        <h2 className="text-3xl font-semibold text-foreground max-md:mb-3">Bill Information</h2>
        <div className="flex flex-wrap items-center gap-3">
          {mahaApproval && mahaApproval !== 'not-reviewed' ? (
            <BillApproval mahaApproval={mahaApproval} />
          ) : (
            <p className="m-0 rounded-full bg-neutral-100 p-0 px-3.5 py-2 font-body text-base leading-6 font-normal text-foreground">
              Not reviewed
            </p>
          )}
          <TrackTag
            billId={bill.id}
            isTracked={isTracked}
            customClassName="relative"
            variant="full"
          />
        </div>
      </div>

      <div className="w-full max-w-[80ch]">
        <h3 className="my-2 text-2xl font-medium text-foreground">Summary</h3>
        <div className="font-body text-base leading-6 font-normal text-foreground">
          {billSummary ? (
            <RichText
              data={billSummary as DefaultTypedEditorState}
              enableGutter={false}
              enableProse={false}
              fontOverride="font-body"
              className="leading-6 [&_h3]:my-2 [&_h3]:text-2xl [&_h3]:font-medium [&_h3]:text-foreground [&_p+p]:mt-[1em]"
            />
          ) : (
            <p className="m-0 p-0 font-body text-base leading-6 font-normal text-foreground">
              No summary available
            </p>
          )}
        </div>
        {bill.bt50?.billID && (
          <div>
            <Button asChild variant="outline" className={'mt-4'}>
              <Link
                href={`https://www.billtrack50.com/billdetail/${bill.bt50.billID}/`}
                target="_blank"
                rel="noopener noreferrer"
              >
                View bill source
              </Link>
            </Button>
          </div>
        )}
        {billStatus ? <BillStatus billStatus={billStatus} /> : null}
        <BillSponsorsSection bill={bill} />
      </div>
    </div>
  )
}
